import React, { useState, useEffect } from 'react';
import './HomePage.css';
import API_BASE_URL from '../config/api';

const ChurchTimes = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    coverPhotoId: null,
    messageId: null
  });
  const [coverPhoto, setCoverPhoto] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [uploading, setUploading] = useState({ image: false, pdf: false });
  const [submitting, setSubmitting] = useState(false);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return null;

    // Check file size (10MB limit) - only for images
    if (type === 'image' && file.size > 10 * 1024 * 1024) {
      showNotification(`File size must be less than 10MB`, 'error');
      return null;
    }

    const endpoint = type === 'image' ? '/api/util/upload/img' : '/api/util/upload/pdf';
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setUploading(prev => ({ ...prev, [type]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: formDataUpload
      });

      if (!response.ok) {
        throw new Error(`${type} upload failed`);
      }

      const data = await response.json();
      return data.id;
    } catch (err) {
      showNotification(`Failed to upload ${type}: ${err.message}`, 'error');
      return null;
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleCoverPhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const uploadId = await handleFileUpload(file, 'image');
      if (uploadId) {
        setCoverPhoto({ file, preview: URL.createObjectURL(file) });
        setFormData(prev => ({ ...prev, coverPhotoId: uploadId }));
      }
    }
  };

  const handlePdfChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const uploadId = await handleFileUpload(file, 'pdf');
      if (uploadId) {
        setPdfFile({ file, name: file.name });
        setFormData(prev => ({ ...prev, messageId: uploadId }));
      }
    }
  };

  const clearCoverPhoto = () => {
    setCoverPhoto(null);
    setFormData(prev => ({ ...prev, coverPhotoId: null }));
  };

  const clearPdfFile = () => {
    setPdfFile(null);
    setFormData(prev => ({ ...prev, messageId: null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.subtitle || !formData.coverPhotoId || !formData.messageId) {
      showNotification('All fields are required', 'error');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/church-times`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to create message');
      }

      showNotification('Message created successfully!', 'success');
      
      // Reset form
      setFormData({ title: '', subtitle: '', coverPhotoId: null, messageId: null });
      setCoverPhoto(null);
      setPdfFile(null);
      
      // Refresh messages list
      fetchMessages();
    } catch (err) {
      showNotification(`Failed to create message: ${err.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/church-times?getAll=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }
      
      const data = await response.json();
      // Sort by creation date (most recent first)
      const sortedMessages = data.sort((a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at));
      setMessages(sortedMessages);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (messageId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/church-times/${messageId}/toggle-active`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to toggle message status');
      }

      showNotification('Message status updated successfully!', 'success');
      fetchMessages();
    } catch (err) {
      showNotification(`Failed to update status: ${err.message}`, 'error');
    }
  };

  const deleteMessage = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/church-times/${messageId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to delete message');
        }

        showNotification('Message deleted successfully!', 'success');
        fetchMessages();
      } catch (err) {
        showNotification(`Failed to delete message: ${err.message}`, 'error');
      }
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const isFormValid = formData.title && formData.subtitle && formData.coverPhotoId && formData.messageId;
  const isUploading = uploading.image || uploading.pdf;

  return (
    <div className="vicar-messages-container">
      {/* Notification */}
      {notification && (
        <div className={`notification ${notification.type}`} style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '10px 20px',
          borderRadius: '4px',
          backgroundColor: notification.type === 'success' ? '#4CAF50' : '#f44336',
          color: 'white',
          zIndex: 1000
        }}>
          {notification.message}
        </div>
      )}

      {/* Create New Message Form */}
      <div className="message-form-section">
        <h3>Create New Message</h3>
        <form onSubmit={handleSubmit} className="message-form">
          <div className="form-row">
            <label className="form-label">Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-row">
            <label className="form-label">Subtitle *</label>
            <input
              type="text"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleInputChange}
              className="form-input"
              required
            />
          </div>

          <div className="form-row">
            <label className="form-label">Cover Photo *</label>
            <div className="file-upload-container">
              <div className="file-input-wrapper">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverPhotoChange}
                  className="file-input"
                  disabled={uploading.image}
                />
                {uploading.image && <span className="upload-spinner">⏳ Uploading...</span>}
              </div>
              {coverPhoto && (
                <div className="file-preview">
                  <img src={coverPhoto.preview} alt="Cover preview" className="image-thumbnail" />
                  <button type="button" onClick={clearCoverPhoto} className="clear-file-btn">✕</button>
                </div>
              )}
            </div>
          </div>

          <div className="form-row">
            <label className="form-label">Message PDF *</label>
            <div className="file-upload-container">
              <div className="file-input-wrapper">
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handlePdfChange}
                  className="file-input"
                  disabled={uploading.pdf}
                />
                {uploading.pdf && <span className="upload-spinner">⏳ Uploading...</span>}
              </div>
              {pdfFile && (
                <div className="file-preview">
                  <span className="pdf-filename">📄 {pdfFile.name}</span>
                  <button type="button" onClick={clearPdfFile} className="clear-file-btn">✕</button>
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="submit-btn"
            disabled={!isFormValid || isUploading || submitting}
          >
            {submitting ? 'Creating Message...' : 'Create Message'}
          </button>
        </form>
      </div>

      {/* Messages List */}
      <div className="messages-list-section">
        <h3>Published Messages</h3>
        {loading ? (
          <div className="loading">Loading messages...</div>
        ) : error ? (
          <div className="error">Error: {error}</div>
        ) : (
          <div className="messages-grid">
            {messages.map(message => (
              <div
                key={message.id}
                className={`message-card ${message.isActive === false ? 'inactive' : ''}`}
              >
                <div className="card-border-wrapper">
                  <div className="card-image">
                    <img src={message.coverPhotoSignedUrl} alt={message.title} />
                  </div>
                  <div className="card-content">
                    <h4>{message.title}</h4>
                    <p>{message.subtitle}</p>
                  </div>
                  <div className="card-actions">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={message.isActive !== false}
                        onChange={() => toggleActive(message.id)}
                      />
                      <span className="toggle-slider"></span>
                      <span className="toggle-label">
                        {message.isActive === false ? 'Inactive' : 'Active'}
                      </span>
                    </label>
                    <button
                      className="delete-btn"
                      onClick={() => deleteMessage(message.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style jsx>{`
        .vicar-messages-container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .message-form-section {
          background: white;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .message-form {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-row {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-weight: 500;
          color: #333;
          font-size: 14px;
        }

        .file-upload-container {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .file-input-wrapper {
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
          width: 100% !important;
          flex-wrap: nowrap !important;
        }

        .file-input {
          flex: 1 1 auto !important;
          min-width: 200px !important;
          max-width: none !important;
          padding: 8px 12px !important;
          border: 1px solid #ccc !important;
          border-radius: 4px !important;
          font-size: 14px !important;
          background: white !important;
          display: inline-block !important;
        }

        .file-input:disabled {
          background: #f5f5f5 !important;
          cursor: not-allowed !important;
        }

        .file-preview {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px;
          background: #f5f5f5;
          border-radius: 4px;
        }

        .image-thumbnail {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 4px;
        }

        .pdf-filename {
          font-size: 14px;
          color: #666;
        }

        .clear-file-btn {
          background: #f44336;
          color: white;
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          cursor: pointer;
          font-size: 12px;
        }

        .submit-btn {
          background: #4CAF50;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
        }

        .submit-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .messages-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
        }

        .message-card {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          transition: opacity 0.3s;
        }

        .message-card.inactive {
          opacity: 0.6;
        }

        .message-card.inactive .card-border-wrapper {
          border: 2px dashed #ccc;
        }

        .card-border-wrapper {
          border: 3px solid;
          border-image: linear-gradient(135deg, #667eea 0%, #764ba2 100%) 1;
          border-radius: 8px;
          overflow: hidden;
        }

        .card-image {
          position: relative;
          width: 100%;
          height: 200px;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        .card-image img {
          max-width: 100%;
          max-height: 100%;
          object-fit: contain;
        }

        .card-content {
          padding: 15px;
          background: white;
        }

        .card-content h4 {
          margin: 0 0 8px 0;
          font-size: 18px;
        }

        .card-content p {
          margin: 0;
          color: #666;
          font-size: 14px;
        }

        .card-actions {
          padding: 15px;
          background: white;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .toggle-switch {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 14px;
        }

        .toggle-switch input[type="checkbox"] {
          display: none;
        }

        .toggle-slider {
          position: relative;
          width: 44px;
          height: 22px;
          background: #ccc;
          border-radius: 22px;
          transition: background 0.3s;
        }

        .toggle-slider::before {
          content: '';
          position: absolute;
          top: 2px;
          left: 2px;
          width: 18px;
          height: 18px;
          background: white;
          border-radius: 50%;
          transition: transform 0.3s;
        }

        .toggle-switch input:checked + .toggle-slider {
          background: #4CAF50;
        }

        .toggle-switch input:checked + .toggle-slider::before {
          transform: translateX(22px);
        }

        .toggle-label {
          font-weight: 500;
          color: #666;
        }

        .delete-btn {
          background: #f44336;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .upload-spinner {
          color: #666;
          white-space: nowrap;
          flex-shrink: 0;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .messages-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default ChurchTimes;